<script lang="ts" setup>
import type { ResponseError } from '#types';
import type { CategoryGroupType, GameSavingType } from '~/types/tasks';

const MAX_LENGTH = 40;

const props = defineProps<{
  categories: CategoryGroupType[];
}>();
const emit = defineEmits<{
  (event: 'load', gameSaving: GameSavingType): void;
}>();
const visible = defineModel<boolean>('visible');

const { t } = useI18n();
const route = useRoute();
const store = useMainStore();
const toast = useCustomToast();
const confirm = useCustomConfirm();

const gameSavings = ref<GameSavingType[]>([]);
const savingName = ref('');

const prepareSaving = () =>
  props.categories.reduce<GameSavingType['value']>(
    (acc, el) => [
      {
        id: el.id,
        value: el.value,
      },
      ...acc.concat(
        el.categories.map((el2) => ({
          id: el2.id,
          value: el2.value,
        })),
      ),
    ],
    [],
  );

const onSave = (value: string) => {
  if (!value || gameSavings.value.length >= 20) return;

  return TaskAPI.createSaving({
    body: {
      label: value.slice(0, MAX_LENGTH),
      value: prepareSaving(),
    },
  })
    .then((data) => {
      gameSavings.value.unshift(data);
      toast.add({
        severity: 'success',
        summary: t('message_saving_saved'),
        life: 3000,
      });
    })
    .catch((error: ResponseError) => {
      if (error.context?.response?.status === 403) {
        toast.add({
          severity: 'error',
          summary: t('message_saving_forbidden'),
          life: 5000,
        });
      }
    });
};

const onLoad = (saving: GameSavingType) => {
  visible.value = false;
  emit('load', saving);
  toast.add({
    severity: 'success',
    summary: t('message_saving_load'),
    life: 3000,
  });
};

const onUpdate = (saving: GameSavingType) => {
  confirm.require({
    header: t('updating_confirm_title'),
    message: t('updating_confirm_message', { label: saving.label }),
    icon: 'pi pi-exclamation-triangle',
    acceptLabel: t('accept'),
    rejectLabel: t('cancel'),
    accept: () => {
      TaskAPI.updateSaving({
        params: { id: saving.id },
        body: { ...saving, value: prepareSaving() },
      }).then((data) => {
        gameSavings.value = gameSavings.value.filter((f) => f.id !== saving.id);
        gameSavings.value.unshift(data);
        toast.add({
          severity: 'success',
          summary: t('message_saving_updated'),
          life: 3000,
        });
      });
    },
  });
};

const onDelete = (saving: GameSavingType) => {
  confirm.require({
    header: t('deleting_confirm_title'),
    message: t('deleting_confirm_message', { label: saving.label }),
    icon: 'pi pi-exclamation-triangle',
    acceptLabel: t('accept'),
    rejectLabel: t('cancel'),
    accept: () => {
      TaskAPI.deleteSaving({ params: { id: saving.id } }).then(() => {
        gameSavings.value = gameSavings.value.filter((f) => f.id !== saving.id);
        toast.add({
          severity: 'success',
          summary: t('message_saving_deleted'),
          life: 3000,
        });
      });
    },
  });
};

watch(visible, () => {
  savingName.value = '';
});

watch(
  () => store.isAuthorized,
  () => {
    if (store.isAuthorized) {
      TaskAPI.savings().then((data) => {
        gameSavings.value = data;
      });
    }
  },
  { immediate: true },
);
</script>

<template>
  <Dialog
    v-if="!store.isAuthorized"
    style="width: 520px"
    dismissable-mask
    :base-z-index="5"
    :draggable="false"
    :header="t('title')"
    modal
    v-model:visible="visible"
  >
    <p class="m-0 leading-6">
      {{ t('should_sign_in') }}
    </p>

    <template #footer>
      <Button
        :label="t('close')"
        icon="pi pi-times"
        @click="visible = false"
        text
      />
      <Button
        as="nuxt-link"
        :to="{ path: '/auth/sign-in', query: { authRedirect: route.fullPath } }"
        :label="t('sign_in')"
        icon="pi pi-sign-in"
        text
      />
    </template>
  </Dialog>

  <DialogSettingSaving
    v-model:visible="visible"
    :title="t('title')"
    :items="gameSavings"
    :limit="store.pro ? 50 : 1"
    @load="onLoad"
    @delete="onDelete"
    @update="onUpdate"
    @create="onSave"
  >
    <template #item="{ data }">
      {{ data.label }}
    </template>

    <template #limit>
      <div>
        <Divider class="!mt-0" />
        <p class="m-0 text-sm mb-3">{{ t('limit_description') }}</p>
        <Button
          @click="store.isSubscriptionDialogVisible = true"
          :label="t('limit_action')"
          outlined
          class="w-full"
        />
      </div>
    </template>
  </DialogSettingSaving>
</template>

<i18n>
en:
  message_saving_saved: Settings saved successfully
  message_saving_load: Save Loaded
  message_saving_updated: Saving updated
  message_saving_deleted: Save deleted
  message_saving_forbidden: Maximum number of saves exceeded
  updating_confirm_title: Updating Save
  updating_confirm_message: Are you sure you want to overwrite save “{label}”?
  deleting_confirm_title: Deleting Save
  deleting_confirm_message: Are you sure you want to delete save “{label}”?
  cancel: Cancel
  close: Close
  accept: Confirm
  sign_in: Sign in
  title: Save settings
  should_sign_in: You must be logged in to be able to save selected game settings.
  limit_description: You have reached the maximum number of saves. Subscription will unlock more opportunities for you!
  limit_action: Get more.

ru:
  message_saving_saved: Настройки успешно сохранены
  message_saving_load: Сохранение загружено
  message_saving_updated: Сохранение обновлено
  message_saving_deleted: Сохранение удалено
  message_saving_forbidden: Превышено максимальное количество соханений
  updating_confirm_title: Обновление сохранения
  updating_confirm_message: Вы уверены, что хотите перезаписать сохранение “{label}”?
  deleting_confirm_title: Удаление сохранения
  deleting_confirm_message: Вы уверены, что хотите удалить сохранение “{label}”?
  cancel: Отмена
  close: Закрыть
  accept: Подтвердить
  sign_in: Войти
  title: Сохранение настроек
  should_sign_in: Для получения возможности сохранять выбранные настройки игры необходимо авторизоваться.
  limit_description: Вы достигли максимального количества сохранений. Подписка разблокирует вам больше возможностей!
  limit_action: Получить больше

de:
  message_saving_saved: Einstellungen erfolgreich gespeichert
  message_saving_load: Speichern geladen
  message_saving_updated: Speichern aktualisiert
  message_saving_deleted: Speichern gelöscht
  message_saving_forbidden: Maximale Anzahl an Speicherungen überschritten
  updating_confirm_title: Update speichern
  updating_confirm_message: Sind Sie sicher, dass Sie die Speicherung „{label}“ überschreiben möchten?
  deleting_confirm_title: Einen Speicherstand löschen
  deleting_confirm_message: Sind Sie sicher, dass Sie den Speicherstand „{label}“ löschen möchten?
  cancel: Stornieren
  close: Schließen
  accept: Anwenden
  sign_in: Login
  title: Einstellungen speichern
  should_sign_in: Um die gewählten Spieleinstellungen speichern zu können, müssen Sie sich anmelden.
  limit_description: Sie haben die maximale Anzahl an Speichern erreicht. Abonnement wird Ihnen mehr Möglichkeiten eröffnen!
  limit_action: Hol mehr.

es:
  message_saving_saved: Configuración guardada exitosamente
  message_saving_load: Guardar cargado
  message_saving_updated: Guardar actualizado
  message_saving_deleted: Guardar eliminado
  message_saving_forbidden: Se superó el número máximo de guardados
  updating_confirm_title: Guardar actualización
  updating_confirm_message: ¿Está seguro de que desea sobrescribir el archivo guardado "{label}"?
  deleting_confirm_title: Eliminar un guardado
  deleting_confirm_message: ¿Está seguro de que desea eliminar el archivo guardado "{label}_"?
  cancel: Cancelar
  close: Cerca
  accept: Aplicar
  sign_in: Acceso
  title: Guardar configuración
  should_sign_in: Para poder guardar la configuración del juego seleccionada, debes iniciar sesión.
  limit_description: Usted ha alcanzado el número máximo de ahorros. La suscripción desbloqueará más oportunidades para usted!
  limit_action: Más.

fr:
  message_saving_saved: Paramètres enregistrés avec succès
  message_saving_load: Enregistrer chargé
  message_saving_updated: Enregistrer mis à jour
  message_saving_deleted: Enregistrer supprimé
  message_saving_forbidden: Nombre maximum de sauvegardes dépassé
  updating_confirm_title: Enregistrer la mise à jour
  updating_confirm_message: Êtes-vous sûr de vouloir écraser la sauvegarde « {label} » ?
  deleting_confirm_title: Supprimer une sauvegarde
  deleting_confirm_message: Etes-vous sûr de vouloir supprimer la sauvegarde « {label} » ?
  cancel: Annuler
  close: Fermer
  accept: Confirmer
  sign_in: Se connecter
  title: Enregistrement des paramètres
  should_sign_in: Pour pouvoir enregistrer les paramètres de jeu sélectionnés, vous devez vous connecter.
  limit_description: Vous avez atteint le nombre maximum d'économies. L'abonnement vous ouvrira d'autres possibilités !
  limit_action: Allez-y.
</i18n>
